import React, { useCallback } from 'react';
import { Box, Card, CardContent } from '@mui/material';
import { Topbar } from '../Topbar.js';
import MeetingsList from '../MeetingsList';
import MeetingSearchBar from '../MeetingSearchBar';
import SortingControls from '../SortingControls';
import LoadingIndicator from '../LoadingIndicator';
import ConfirmationDialog from '../ConfirmationDialog';
import ShareDialog from '../ShareDialog';
import { tutoMainPage } from '../tutoMainPage.js';
import { getSentence } from '../translations.js';
import { useMeetingContext } from '../../context/MeetingContext';

const MobileLayout = ({
  // Mobile swipe props
  containerRef,
  activePanelIndex,
  isAnimating,
  handleTouchStart,
  handleTouchMove,
  handleTouchEnd,
  handleTouchCancel,
  switchToLeftPanel,
  
  // Meeting data
  meetings,
  processedMeetings,
  selectedMeetingId,
  sortingLoading,
  
  // Dialog state
  pendingDeleteId,
  pendingShareId,
  
  // Event handlers
  onMeetingSelect,
  onMeetingDelete,
  onMeetingTogglePin,
  onMeetingShare,
  onDeleteConfirm,
  onDeleteCancel,
  onShareClose,
  onFileUploadSuccess,
  loadMeetings, // Add loadMeetings for sorting changes
  
  // Accordion state
  pinnedAccordionExpanded,
  regularAccordionExpanded,
  onPinnedAccordionChange,
  onRegularAccordionChange,
  
  // Right panel content
  renderRightPanel
}) => {
  // Use meeting context for search functionality and sorting
  const {
    searchResults,
    isSearching,
    isSearchActive,
    searchMeetings: handleSearch,
    clearSearch: handleClearSearch,
    getDisplayMeetings,
    sortBy,
    sortDescending,
    setSortParams
  } = useMeetingContext();

  // Handle sorting changes (client-side only, no API calls)
  const handleOrderByChange = useCallback((newOrderBy) => {
    setSortParams(newOrderBy, sortDescending);
  }, [setSortParams, sortDescending]);

  const handleDescendingChange = useCallback((newDescending) => {
    setSortParams(sortBy, newDescending);
  }, [setSortParams, sortBy]);

  // Use search results if available, otherwise use processed meetings
  const displayMeetings = getDisplayMeetings();

  return (
    <div style={{ height: "100vh", overflow: "hidden" }}>
      <Topbar 
        onFileUploadSuccess={onFileUploadSuccess} 
        onMobileHomePress={switchToLeftPanel}
        activePanelIndex={activePanelIndex}
      />
      
      {/* Dialogs */}
      <ConfirmationDialog
        open={!!pendingDeleteId}
        title={getSentence(6)}
        message={getSentence(7)}
        confirmText={getSentence(9)}
        cancelText={getSentence(8)}
        onConfirm={onDeleteConfirm}
        onCancel={onDeleteCancel}
      />

      <ShareDialog
        open={!!pendingShareId}
        meetingId={pendingShareId}
        onClose={onShareClose}
      />

      {/* Mobile Sliding Panels Container */}
      <div 
        style={{ 
          height: 'calc(100vh - 64px)', // Account for topbar height
          width: '100%',
          position: 'relative',
          overflow: 'hidden'
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchCancel}
      >
        {/* Sliding panels container */}
        <div 
          ref={containerRef}
          style={{ 
            display: 'flex', 
            height: '100%', 
            width: '200vw', // Exactly twice the viewport width
            transform: `translateX(${activePanelIndex * -100}vw)`,
            transition: isAnimating ? 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)' : 'none',
          }}
        >
          {/* Meetings List Panel (Left) */}
          <div style={{ 
            width: '100vw', // Exactly viewport width
            height: '100%',
            backgroundColor: '#ddd',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            flexShrink: 0, // Prevent shrinking
            flexGrow: 0,   // Prevent growing
          }}>
            <Box sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              flexDirection: "column"
            }}>
              {/* Search Bar - Fixed at top */}
              {meetings.length > 0 && (
                <Box>
                  <MeetingSearchBar
                    onSearch={handleSearch}
                    onClear={handleClearSearch}
                    isSearching={isSearching}
                    searchResults={searchResults}
                    orderBy={sortBy}
                    descending={sortDescending}
                  />
                  {/* Sorting Controls - Always visible */}
                  <SortingControls
                    orderBy={sortBy}
                    descending={sortDescending}
                    onOrderByChange={handleOrderByChange}
                    onDescendingChange={handleDescendingChange}
                    disabled={isSearching}
                  />
                </Box>
              )}
              
              {/* Meetings List - Scrollable below search */}
              <Box sx={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
                {/* Unified Loading Indicator at top of meeting list */}
                <LoadingIndicator
                  isLoading={isSearching}
                  message="Searching..."
                  size="small"
                  position="inline"
                  style={{
                    justifyContent: 'center',
                    padding: '12px 0',
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderBottom: '1px solid #f0f0f0',
                    backdropFilter: 'blur(4px)'
                  }}
                />
                
                {displayMeetings.length > 0 ? (
                  <MeetingsList
                    meetings={displayMeetings}
                    selectedMeetingId={selectedMeetingId}
                    pinnedAccordionExpanded={pinnedAccordionExpanded}
                    regularAccordionExpanded={regularAccordionExpanded}
                    onMeetingSelect={onMeetingSelect}
                    onMeetingDelete={onMeetingDelete}
                    onMeetingTogglePin={onMeetingTogglePin}
                    onMeetingShare={onMeetingShare}
                    onPinnedAccordionChange={onPinnedAccordionChange}
                    onRegularAccordionChange={onRegularAccordionChange}
                  />
                ) : searchResults !== null && searchResults.length === 0 && !isSearching ? (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    height: '100%',
                    color: '#999',
                    fontStyle: 'italic'
                  }}>
                    No meetings found matching your search criteria.
                  </Box>
                ) : !isSearchActive && meetings.length === 0 ? (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  height: '100%',
                  padding: '20px',
                }}>
                  <Card 
                    elevation={3}
                    sx={{ 
                      maxWidth: 600, 
                      width: '100%',
                      borderRadius: 2
                    }}
                  >
                    <CardContent sx={{ padding: 3 }}>
                      {tutoMainPage()}
                    </CardContent>
                  </Card>
                </div>
                ) : null}
              </Box>
            </Box>
          </div>
          
          {/* Right Panel (Content) */}
          <div style={{ 
            width: '100vw', // Exactly viewport width
            height: '100%',
            backgroundColor: '#EBEBEB',
            overflowY: 'auto',
            display: 'flex',
            alignItems: 'flex-start',
            flexShrink: 0, // Prevent shrinking
            flexGrow: 0,   // Prevent growing
          }}>
            {renderRightPanel()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileLayout;